import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowLeft, MagnifyingGlass, UsersThree, EnvelopeSimple, Phone, NotePencil, PaperPlaneTilt } from "@phosphor-icons/react";
import { BrutalButton, BrutalInput, BrutalTextarea } from "../components/Brutal";
import api, { formatApiError } from "../api";
import { useAuth } from "../AuthContext";

/**
 * v406.12 — Customer Directory. Every repeat store customer (auto-captured at
 * checkout): who they are, how often they buy, and how much they've spent.
 * RBAC-scoped by the backend: owner sees the org, distributors their own.
 */
export default function CustomersPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [noteFor, setNoteFor] = useState(null); // customer id being edited
  const [noteDraft, setNoteDraft] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [blastOpen, setBlastOpen] = useState(false); // v406.15 email blast
  const canBlast = !!user?.distributor_code;

  const saveNote = async (c) => {
    setSavingNote(true);
    try {
      const { data: res } = await api.patch(`/store-customers/${c.id}`, { note: noteDraft });
      setData((d) => ({ ...d, items: d.items.map((x) => (x.id === c.id ? res.row : x)) }));
      setNoteFor(null);
      toast.success(t("common.saved"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingNote(false);
    }
  };

  const isElevatedView = ["owner", "admin", "cfo", "partner"].includes(user?.role);
  const fmt$ = (n) =>
    `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtDate = (iso) =>
    iso ? new Date(iso).toLocaleDateString(i18n.language === "ru" ? "ru-RU" : "en-US",
      { year: "numeric", month: "short", day: "numeric" }) : "—";

  useEffect(() => {
    const timer = setTimeout(() => {
      api.get("/store-customers", { params: q.trim() ? { q: q.trim() } : {} })
        .then(({ data }) => setData(data))
        .catch((e) => toast.error(formatApiError(e)));
    }, 250);
    return () => clearTimeout(timer);
  }, [q]);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-4" data-testid="customers-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <Link to="/" className="inline-flex items-center gap-1 text-xs font-medium text-zinc-500 hover:text-zinc-800 mb-1">
            <ArrowLeft size={12} weight="bold" /> {t("common.back")}
          </Link>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight flex items-center gap-2">
            <UsersThree size={26} weight="bold" className="text-[#8a6a1c]" /> {t("customers.title")}
          </h1>
          <p className="text-sm text-zinc-600 mt-0.5">{t("customers.subtitle")}</p>
        </div>
        {canBlast && (
          <BrutalButton size="sm" onClick={() => setBlastOpen(true)}
            disabled={!data || data.total === 0} data-testid="customers-blast-btn">
            <PaperPlaneTilt size={13} weight="bold" /> {t("customers.blast_btn")}
          </BrutalButton>
        )}
      </div>

      <div className="relative">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("customers.search")}
          className="w-full h-11 pl-9 pr-4 rounded-xl border border-zinc-200 text-sm focus:outline-none focus:border-[#c9a24a] focus:ring-2 focus:ring-[#c9a24a]/20 bg-white"
          data-testid="customers-search"
        />
      </div>

      {!data ? (
        <div className="text-sm text-zinc-500 bg-zinc-50 border border-zinc-100 rounded-md p-4">{t("common.loading")}</div>
      ) : data.items.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-xl p-6 text-center" data-testid="customers-empty">
          <p className="text-sm font-medium text-zinc-900">{t("customers.empty")}</p>
          <p className="text-xs text-zinc-500 mt-1">{t("customers.empty_hint")}</p>
        </div>
      ) : (
        <>
          <p className="text-xs text-zinc-500" data-testid="customers-count">
            {t("customers.count", { n: data.total })}
          </p>
          <div className="space-y-2">
            {data.items.map((c) => (
              <div key={`${c.distributor_id}-${c.customer_email}`} data-testid={`customer-${c.customer_email}`}
                className="bg-white border border-zinc-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-zinc-900">{c.customer_name}</p>
                    {c.orders_count > 1 && (
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full border bg-[#fbf6e7] text-[#8a6a1c] border-[#c9a24a]/60">
                        {t("customers.repeat")}
                      </span>
                    )}
                    {isElevatedView && c.distributor_code && (
                      <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded border border-zinc-200 text-zinc-500">
                        {c.distributor_code}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-600 mt-1 flex items-center gap-3 flex-wrap">
                    <span className="inline-flex items-center gap-1"><EnvelopeSimple size={12} /> {c.customer_email}</span>
                    {c.customer_contact && (
                      <span className="inline-flex items-center gap-1"><Phone size={12} /> {c.customer_contact}</span>
                    )}
                  </div>
                  <p className="text-[11px] text-zinc-400 mt-1">
                    {t("customers.last_order", { date: fmtDate(c.last_order_at), ref: c.last_reference })}
                  </p>
                  {/* v406.14 — private customer notes */}
                  {noteFor === c.id ? (
                    <div className="mt-2 space-y-1.5">
                      <textarea
                        value={noteDraft}
                        onChange={(e) => setNoteDraft(e.target.value)}
                        rows={2}
                        maxLength={2000}
                        placeholder={t("customers.note_placeholder")}
                        className="w-full text-xs border border-zinc-200 rounded-lg p-2 focus:outline-none focus:border-[#c9a24a]"
                        data-testid={`customer-note-input-${c.customer_email}`}
                      />
                      <div className="flex gap-2">
                        <button type="button" disabled={savingNote} onClick={() => saveNote(c)}
                          className="text-[11px] font-bold text-white bg-zinc-900 rounded-md px-2.5 py-1"
                          data-testid={`customer-note-save-${c.customer_email}`}>
                          {savingNote ? t("common.saving") : t("common.save")}
                        </button>
                        <button type="button" onClick={() => setNoteFor(null)}
                          className="text-[11px] font-medium text-zinc-500 px-1">
                          {t("common.cancel")}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button type="button"
                      onClick={() => { setNoteFor(c.id); setNoteDraft(c.note || ""); }}
                      className="mt-1.5 inline-flex items-start gap-1 text-left text-[11px] text-zinc-500 hover:text-zinc-800"
                      data-testid={`customer-note-btn-${c.customer_email}`}>
                      <NotePencil size={12} className="mt-0.5 shrink-0 text-[#8a6a1c]" />
                      {c.note
                        ? <span className="italic">&ldquo;{c.note}&rdquo;</span>
                        : <span className="underline decoration-dotted">{t("customers.add_note")}</span>}
                    </button>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <p className="text-base font-semibold tabular-nums text-zinc-900">{fmt$(c.total_spent)}</p>
                  <p className="text-[11px] text-zinc-500">{t("customers.orders_n", { n: c.orders_count })}</p>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {blastOpen && (
        <BlastDialog
          total={data?.total || 0}
          storeName={user?.name}
          onClose={() => setBlastOpen(false)}
          t={t}
        />
      )}
    </div>
  );
}

function BlastDialog({ total, storeName, onClose, t }) {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async (e) => {
    e.preventDefault();
    if (message.trim().length < 5) { toast.error(t("customers.blast_too_short")); return; }
    if (!window.confirm(t("customers.blast_confirm", { n: total }))) return;
    setBusy(true);
    try {
      const { data } = await api.post("/store-customers/blast", {
        subject: subject.trim() || null,
        message: message.trim(),
      });
      toast.success(t("customers.blast_sent", { n: data.sent_count }));
      onClose();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={send} data-testid="blast-dialog"
        className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5 space-y-3">
        <h2 className="font-semibold text-lg">{t("customers.blast_title")}</h2>
        <p className="text-xs text-zinc-500">{t("customers.blast_hint", { n: total })}</p>
        <div>
          <label className="text-xs font-medium text-zinc-600">{t("customers.blast_subject")}</label>
          <BrutalInput value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={150}
            placeholder={t("customers.blast_subject_ph", { name: storeName || "AXIS" })}
            data-testid="blast-subject-input" />
        </div>
        <div>
          <label className="text-xs font-medium text-zinc-600">{t("customers.blast_message")}</label>
          <BrutalTextarea rows={5} value={message} onChange={(e) => setMessage(e.target.value)} maxLength={4000}
            placeholder={t("customers.blast_message_ph")} data-testid="blast-message-input" />
        </div>
        <p className="text-[11px] text-zinc-400">{t("customers.blast_footer_note")}</p>
        <div className="flex justify-end gap-2 pt-1">
          <BrutalButton type="button" variant="secondary" onClick={onClose} data-testid="blast-cancel">
            {t("common.cancel")}
          </BrutalButton>
          <BrutalButton type="submit" disabled={busy} data-testid="blast-send">
            <PaperPlaneTilt size={13} weight="bold" /> {busy ? t("common.saving") : t("customers.blast_send", { n: total })}
          </BrutalButton>
        </div>
      </form>
    </div>
  );
}
